import React, { useMemo, useRef } from 'react';
import { type Traits, WidgetType } from '@leagueplatform/masonry-api';
import {
  Flex,
  Overline,
  SubtitleOne,
  HeadingThree,
  Text,
  Box,
} from '@leagueplatform/genesis-commons';
import type { MasonryEngineNode } from '@leagueplatform/masonry-engine';
import {
  StackLayout,
  ConditionalLinkCard,
  ConditionalLink,
} from '@leagueplatform/web-common-components';
import { ParagraphText } from '@leagueplatform/genesis-core';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import {
  BadgeList,
  BadgeListProps,
} from '#common/badge-list/badge-list.component.tsx';
import { SquareImageContainer } from '#common/square-image-container/square-image-container.component.tsx';
import { useGetTraitsForMedia } from '../../hooks/use-get-traits-for-media.hook';

export interface SmallVisualCardWidgetProps {
  image: string;
  overline?: string;
  title: string;
  description: string;
  imageAltText: string;
  imageLoading?: 'lazy' | 'eager';
  badges?: BadgeListProps['badges'];
  linkUrl?: string;
  isChildItem?: boolean;
  onCardClick?: (e: React.MouseEvent) => void;
  traits?: Traits;
}

export type SmallVisualCardWSNode = MasonryEngineNode<
  WidgetType.SMALL_VISUAL_CARD,
  SmallVisualCardWidgetProps
>;

const SquareImage = ({
  image,
  imageAltText,
  isChildItem,
  imageLoading = 'lazy',
}: {
  image: string;
  imageAltText: string;
  isChildItem: boolean;
  imageLoading?: 'lazy' | 'eager';
}) => (
  <SquareImageContainer
    src={image}
    alt={imageAltText}
    maxWidth="9.8rem"
    maxWidthDesktop={isChildItem ? '9.8rem' : '11.2rem'}
    loading={imageLoading}
  />
);

export const SmallVisualCardWidget = ({
  overline,
  title,
  description,
  image,
  imageLoading = 'lazy',
  badges,
  imageAltText,
  linkUrl,
  isChildItem = false,
  onCardClick = () => {},
  traits,
}: SmallVisualCardWidgetProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const overlineId = `svc-${title.replace(/\s+/g, '-')}`;
  // TODO: Traits will be removed in Masonry 2.0
  const traitItems = useGetTraitsForMedia(traits);
  const iconAlign = traitItems?.iconAlignment || 'right';

  const textAlign = useMemo(
    () =>
      iconAlign === 'trailing' || iconAlign === 'right'
        ? 'flex-start'
        : 'flex-end',
    [iconAlign],
  );

  return (
    <ConditionalLinkCard
      linkRef={linkRef}
      hasLink={Boolean(linkUrl)}
      cardStyle={{
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        padding: 'oneAndHalf',
        alignItems: 'center',
        backgroundColor:
          traitItems?.backgroundColor || 'surface.card.secondary',
      }}
    >
      {/* Add the icon image at flex start when trait for iconAlign is 'leading' */}
      {(iconAlign === 'left' || iconAlign === 'leading') && (
        <Flex
          alignItems="center"
          justifyContent="flex-start"
          marginInlineEnd={{
            _: 'threeQuarters',
            desktop: 'two',
          }}
        >
          <SquareImage
            image={image}
            imageAltText={imageAltText}
            isChildItem={isChildItem}
            imageLoading={imageLoading}
          />
        </Flex>
      )}
      <Flex flexDirection="column" alignItems={textAlign} flex="2">
        <StackLayout space="$half">
          {overline && (
            <Overline id={overlineId} as={Text} color="onSurface.text.subdued">
              {overline}
            </Overline>
          )}
          <StackLayout space="$quarter">
            <SubtitleOne
              as={HeadingThree}
              fontWeight="bold"
              aria-describedby={overlineId}
            >
              <ConditionalLink
                ref={linkRef}
                url={linkUrl}
                onClick={onCardClick}
              >
                {title}
              </ConditionalLink>
            </SubtitleOne>
            <ParagraphText htmlStringOptions={BodyTextHtmlTagOptions}>
              {description}
            </ParagraphText>
          </StackLayout>
        </StackLayout>
        {badges && (
          <Box marginTop="threeQuarters">
            <BadgeList
              badges={badges}
              backgroundColor={traitItems?.badgeBackgroundColor}
            />
          </Box>
        )}
      </Flex>
      {/* Add the icon image at flex end when trait for iconAlign is 'trailing' */}
      {(iconAlign === 'right' || iconAlign === 'trailing') && (
        <Flex
          alignItems="center"
          justifyContent="flex-end"
          marginInlineStart={{
            _: 'threeQuarters',
            desktop: 'two',
          }}
        >
          <SquareImage
            image={image}
            imageAltText={imageAltText}
            isChildItem={isChildItem}
            imageLoading={imageLoading}
          />
        </Flex>
      )}
    </ConditionalLinkCard>
  );
};
